/**
 * 실제 프롬프트(actualPrompt) 섹션 분할 유틸리티
 *
 * 어드민 보고서 뷰어에서 LLM에 전달된 전체 입력 프롬프트를
 * 시스템 / 환자·처방 / 허가사항 / 가이드라인 / 항콜린 점수 창으로 구분한다.
 *
 * 설계 원칙:
 * - 닫는 태그가 없거나(`</처방정보>` 부재) `>>` 오타가 있을 수 있어, 닫는 태그를 일절 사용하지 않는다.
 * - 각 섹션 경계는 "알려진 시작 앵커 태그"의 위치로만 결정한다(시작 태그는 버전 간 불변).
 * - 매칭이 없거나 일부만 매칭돼도 오류 없이 동작한다(무손실 폴백).
 *
 * 주의: AI '출력'(보고서/복약지도문) 분할은 sectionParser.ts(parseSectionContent)가 담당한다.
 *       본 파서는 LLM '입력 프롬프트' 전용이다.
 */

export type PromptSectionKey =
  | "system"
  | "patient"
  | "license"
  | "guideline"
  | "anticholinergic"
  | "full";

export interface PromptSection {
  /** 섹션 종류 키 */
  key: PromptSectionKey;
  /** UI 표시용 한글 라벨 */
  label: string;
  /** 해당 구간 원문 (앞뒤 공백 보존) */
  content: string;
  /** 구간 줄 수 (UI 규모 표기용) */
  lineCount: number;
}

type AnchorPane = Exclude<PromptSectionKey, "system" | "full">;

/**
 * pane 별 시작 앵커 태그(열 0 단독 줄).
 * 가이드라인은 종류·태그명이 매번 다르므로 화이트리스트로 관리한다.
 * 신종 가이드라인 태그가 추가되면 아래 guideline.tags 에만 한 줄 추가하면 된다.
 */
const SECTION_ANCHORS: { pane: AnchorPane; tags: string[] }[] = [
  { pane: "patient", tags: ["환자정보", "처방정보"] },
  { pane: "license", tags: ["허가사항"] },
  {
    pane: "guideline",
    tags: [
      "comprehensive_medication_management_review_tool", // CMM
      "long_term_care_elderly_cmm_review_tool", // LCMM (요양시설 노인)
      "BEERS_criteria_2023", // Beers Criteria 2023
      "document", // STOPP/START v3: <document id="SPSTv3" ...>
      "KDGERI", // KDGERI
    ],
  },
  { pane: "anticholinergic", tags: ["anticholinergic_scores"] },
];

export const PROMPT_SECTION_LABELS: Record<PromptSectionKey, string> = {
  system: "시스템 프롬프트",
  patient: "환자정보 / 처방정보",
  license: "허가사항",
  guideline: "가이드라인 (CMM/LCMM 등)",
  anticholinergic: "항콜린 점수",
  full: "전체 프롬프트 (미분할)",
};

/**
 * 줄이 "여는 시작 태그"로 시작하면 태그명을 추출한다.
 * - 한글 태그(환자정보)와 영문 태그 모두 매칭
 * - 속성 허용: `<document id="...">` → "document"
 * - 같은 줄에 내용/닫는 태그가 이어져도 인식: `<KDGERI>g</KDGERI>` → "KDGERI"
 * - 닫는 태그(`</...>`)는 '<' 다음 '/'라 불일치 → 무시
 * - 태그명 뒤가 공백/`>`/줄끝이어야 함(부분 단어 오탐 방지). 화이트리스트로 한 번 더 걸러진다.
 */
const matchOpenTag = (line: string): string | null => {
  const m = /^<([\p{L}_][\p{L}\p{N}_-]*)(?=[\s>]|$)/u.exec(line);
  return m ? m[1] : null;
};

export const parsePromptSections = (prompt: string): PromptSection[] => {
  if (!prompt) return [];
  const lines = prompt.split("\n");

  const tagToPane = new Map<string, AnchorPane>();
  SECTION_ANCHORS.forEach(({ pane, tags }) => tags.forEach((tag) => tagToPane.set(tag, pane)));

  // 1) 알려진 시작 앵커의 위치를 순서대로 수집
  const hits: { pane: AnchorPane; line: number }[] = [];
  for (let i = 0; i < lines.length; i++) {
    const tag = matchOpenTag(lines[i]);
    if (tag) {
      const pane = tagToPane.get(tag);
      if (pane) hits.push({ pane, line: i });
    }
  }

  const toSection = (key: PromptSectionKey, from: number, to: number): PromptSection => {
    const slice = lines.slice(from, to);
    return {
      key,
      label: PROMPT_SECTION_LABELS[key],
      content: slice.join("\n"),
      lineCount: slice.length,
    };
  };

  // 2) 폴백: 매칭 0건(미지 버전) → 분할하지 않고 전체 단일 섹션
  if (hits.length === 0) {
    return [toSection("full", 0, lines.length)];
  }

  const sections: PromptSection[] = [];

  // 3) 시스템 프롬프트 = 첫 앵커 이전 (비공백일 때만)
  if (hits[0].line > 0) {
    const head = toSection("system", 0, hits[0].line);
    if (head.content.trim()) sections.push(head);
  }

  // 4) 연속된 동일 pane 앵커는 한 구간으로 병합 (환자정보+처방정보, 다중 가이드라인 등)
  for (let i = 0; i < hits.length; ) {
    const pane = hits[i].pane;
    const start = hits[i].line;
    let j = i;
    while (j + 1 < hits.length && hits[j + 1].pane === pane) j++;
    const end = j + 1 < hits.length ? hits[j + 1].line : lines.length;
    sections.push(toSection(pane, start, end));
    i = j + 1;
  }

  return sections;
};
